import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import { BLOG_POSTS, getRecentBlogPosts } from '../data/blog';
import { BlogPost } from '../data/blog';

const categoryLabels: Record<string, string> = {
  handyvertrag: 'Handyvertrag',
  smartphone: 'Smartphone',
  internet: 'Internet',
  'strom-gas': 'Strom & Gas',
  ratgeber: 'Ratgeber',
};

const categoryColors: Record<string, string> = {
  handyvertrag: 'bg-blue-100 text-blue-700',
  smartphone: 'bg-green-100 text-green-700',
  internet: 'bg-orange-100 text-orange-700',
  'strom-gas': 'bg-yellow-100 text-yellow-700',
  ratgeber: 'bg-gray-100 text-gray-700',
};

const BlogListPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredPosts = BLOG_POSTS.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = ['all', 'handyvertrag', 'smartphone', 'internet', 'strom-gas', 'ratgeber'];

  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Blog & Ratgeber - Handyvertrag, Smartphone & Internet Tipps | Easy Vertrag</title>
        <meta name="description" content="Aktuelle Ratgeber und Vergleiche zu Handyverträgen, Smartphones, Internet und Strom. Tipps zum Sparen und besten Angebote 2026." />
        <meta name="keywords" content="handyvertrag blog, smartphone ratgeber, mobilfunk tipps, internet vergleich blog" />
        <link rel="canonical" href="https://easy4vertrag.de/blog" />
        <meta property="og:title" content="Blog & Ratgeber | Easy Vertrag" />
        <meta property="og:description" content="Aktuelle Ratgeber und Vergleiche zu Handyverträgen, Smartphones, Internet und Strom." />
        <meta property="og:url" content="https://easy4vertrag.de/blog" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Easy Vertrag Blog",
            "url": "https://easy4vertrag.de/blog",
            "description": "Ratgeber und Vergleiche zu Handyverträgen, Smartphones, Internet und Strom."
          }
        `}</script>
      </Helmet>

      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">Blog & Ratgeber</h1>
            <p className="text-xl text-blue-100 mb-6">
              Tipps und Vergleiche zu Handyverträgen, Smartphones, Internet und Energie
            </p>
            <div className="relative max-w-xl mx-auto">
              <input
                type="text"
                placeholder="Artikel durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-5 py-3 pl-12 rounded-full text-gray-800 bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'Alle Artikel' : categoryLabels[cat]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Keine Artikel gefunden.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, i) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  whileHover={{ y: -6, boxShadow: '0 16px 32px rgba(0,0,0,0.1)' }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[post.category]}`}>
                        {categoryLabels[post.category]}
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readingTime} Min
                        </span>
                      </div>
                      <h2 className="text-lg font-bold text-gray-800 mb-2 leading-snug">{post.title}</h2>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                      <span className="inline-flex items-center text-blue-600 font-medium text-sm group">
                        Weiterlesen
                        <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogListPage;
